import { type Mandator } from '@einwert/valuation-api-client'
import {
    useFetchNewOrderDocuments,
    useGetExternalParticipants,
    useGetOrderOverview,
    usePatchOrder,
    useUploadOrderDocuments,
    useUploadOrderImages,
} from 'ClientPortal/src/api/Queries/orders'
import { useGetSortedAndFilteredThreadsWithMetadata } from 'ClientPortal/src/api/Queries/threads'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { isoDateStringToLocalFormat, joinDefinedStrings, valueOrDefault } from 'ComponentLibrary'
import type { Contact as ContactSchema } from '@einwert/valuation-api-client'
import { isEmpty } from 'lodash'
import { TabularInfoProps } from 'ComponentLibrary/src/molecules/TabularInfo/TabularInfo'
import { Document } from 'ComponentLibrary/src/molecules/FileUploadModal/types'
import { localizeSupportedDocumentTags } from 'ComponentLibrary/src/molecules/SelectTags/constants'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { useGetAllOrderDocumentsZipUrl, useGetAllOrderImagesZipUrl } from 'ClientPortal/src/api/Queries/files'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'
import { useGetAppraiserUsers, useGetCustomerUsers } from 'ClientPortal/src/api/Queries/users'
import { useIamContext } from 'ClientPortal/src/context/IAmContext'
import { useGetPlausibility } from 'ClientPortal/src/api/Queries/plausibility'
import { useGetValueChecks } from 'ClientPortal/src/api/Queries/value-checks'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export const useOrderDetailsPageData = ({ onPatchAssignedCustomerUsersSuccess }: { onPatchAssignedCustomerUsersSuccess?: () => void }) => {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.orderDetailsPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const { orderId } = useTypedParams(['orderId'])
    const { orderOverview, isOrderOverviewPending } = useGetOrderOverview({ throwOnError: throwOnForbiddenApiError })
    const { customerUsers, isCustomerUsersPending } = useGetCustomerUsers({ orderId })
    const { appraiserUsers, isAppraiserUsersPending } = useGetAppraiserUsers({ orderId })
    const { externalParticipants: externalParticipantsView, isExternalParticipantsInitialLoading } = useGetExternalParticipants({ orderId })
    const showPlausibility = !!orderOverview && !orderOverview.external_participant_mailbox
    const { fetchNewOrderDocuments, isFetchingNewOrderDocuments } = useFetchNewOrderDocuments()
    const { user: currentUser } = useIamContext()
    const latestDraftId = orderOverview?.drafts.at(-1)
    const { plausibility: latestDraftPlausibility, isPlausibilityPending: isLatestDraftPlausibilityPending } = useGetPlausibility({
        valuationId: latestDraftId,
        enabled: !!latestDraftId && showPlausibility,
    })
    const shouldFetchValueChecksForOrder = orderOverview?.valuation_reason === 'intrinsic_value_check'
    const { valueChecks } = useGetValueChecks({ assetId: shouldFetchValueChecksForOrder ? orderOverview?.asset_uuid : undefined })
    const valueCheckForOrder = valueChecks.find((valueCheck) => valueCheck.order_uuid === orderOverview?.uuid)

    const { isUploadingOrderImages, uploadOrderImages, failedImageUploads, numImagesToUpload } = useUploadOrderImages({
        orderId,
    })
    const { isUploadingOrderDocuments, uploadOrderDocuments, failedDocumentUploads, numDocumentsToUpload } = useUploadOrderDocuments({
        orderId,
    })
    const { isPatchingOrder, patchOrder } = usePatchOrder({ orderId })
    const { isPatchingOrder: isPatchingAssignedCustomerUsers, patchOrder: patchAssignedCustomerUsers } = usePatchOrder({
        orderId,
        onSuccess: onPatchAssignedCustomerUsersSuccess,
    })
    const { patchOrder: patchAssignedObservers, isPatchingOrder: isAssignedObserversPending } = usePatchOrder({
        orderId,
    })

    const getImages = () => orderOverview?.images ?? []

    const documents =
        orderOverview?.documents.map((document, index) => ({
            ...document,
            index,
            tags: localizeSupportedDocumentTags(document.tags ?? [], locale),
        })) ?? []

    const deleteOrderImage = (imageIndex: number) =>
        patchOrder([
            {
                op: 'remove',
                path: `/images/${imageIndex}`,
            },
        ])

    const deleteOrderDocument = (documentIndex: number) =>
        patchOrder([
            {
                op: 'remove',
                path: `/documents/${documentIndex}`,
            },
        ])

    const downloadDocument = async ({ documentId, orderId }: { documentId: string; orderId: UUID }) => {
        // Since the signed url for the documents are only valid for a few minutes we need to refetch them first
        const response = await fetchNewOrderDocuments({ orderId })
        const document = response?.documents?.find((document) => document.uuid === documentId)

        if (document) {
            window.open(document.url, '_self')
        }
    }

    const updateDocuments = (documents: Document[], onSuccess: () => void) => {
        const transformedDocuments = documents.map((document) => ({
            ...document,
            tags: document.tags?.map((tag) => tag.title) ?? [],
        }))

        patchOrder(
            [
                {
                    op: 'replace',
                    path: '/documents',
                    value: transformedDocuments,
                },
            ],
            { onSuccess }
        )
    }

    const dateOrDefault = (value: string | undefined, defaultValue = '-') =>
        isoDateStringToLocalFormat(value, false, locale) || defaultValue
    const getUsageTypeLabel = ({ usage_type, sub_usage_type }: { usage_type: string; sub_usage_type?: string }) => {
        const usageTypeLabel = tTerminology(`usageTypes.${usage_type}`, { defaultValue: usage_type })

        if (!sub_usage_type) {
            return usageTypeLabel
        }

        const subUsageTypeLabel = tTerminology(`usageTypes.${sub_usage_type}`, { defaultValue: '' })
        if (subUsageTypeLabel) {
            return subUsageTypeLabel
        }

        return usage_type === 'other' ? sub_usage_type : usageTypeLabel
    }

    const getOrderKeyFigures = (): TabularInfoProps['rows'] => {
        if (!orderOverview) {
            return []
        }
        const costCenter = orderOverview.cost_center
        const { name: costCenterName, description: costCenterDescription } = costCenter || {}
        const costCenterLine = costCenter ? joinDefinedStrings([`${costCenterName}`, costCenterDescription], ' | ') : undefined

        return [
            ...(orderOverview.cost_center
                ? [
                      {
                          label: t('orderData.costCenter'),
                          value: costCenterLine || '-',
                      },
                  ]
                : []),
            ...(orderOverview.note
                ? [
                      {
                          label: t('orderData.additionalInformation'),
                          value: orderOverview.note,
                      },
                  ]
                : []),
            {
                label: t('orderData.valuationReason'),
                value: orderOverview.valuation_reason
                    ? tTerminology(`valuationReasons.${orderOverview.valuation_reason}`, {
                          defaultValue: orderOverview.valuation_reason,
                      })
                    : t('noPurpose'),
                greyedOut: !orderOverview.valuation_reason,
            },
            {
                label: t('orderData.orderDate'),
                value: dateOrDefault(orderOverview.order_date),
            },
            {
                label: t('orderData.dueDate'),
                value: dateOrDefault(orderOverview.due_date, t('orderData.dueDateFallback')),
                greyedOut: !orderOverview.due_date,
                tooltip: orderOverview.due_date && t('orderData.dueDateTooltip'),
            },
            {
                label: t('orderData.valuationDate'),
                value: dateOrDefault(orderOverview.valuation_date, t('notSet')),
                greyedOut: !orderOverview.valuation_date,
            },
            {
                label: t('orderData.usageType'),
                value: orderOverview.asset_usage_types
                    ? orderOverview.asset_usage_types.map(getUsageTypeLabel).join(', ')
                    : t('noInformation'),
                greyedOut: !orderOverview.asset_usage_types || isEmpty(orderOverview.asset_usage_types),
            },
            {
                label: t('orderData.assetState'),
                value: orderOverview.asset_state
                    ? tTerminology(`assetStates.${orderOverview.asset_state}`, { defaultValue: orderOverview.asset_state })
                    : t('notSet'),
                greyedOut: !orderOverview.asset_state,
            },
        ].filter((item) => item.value)
    }

    const getValuationKeyFigures = (): TabularInfoProps['rows'] => {
        if (!orderOverview) {
            return []
        }

        const serviceProvider = orderOverview.service_provider?.name || orderOverview.external_appraising_entity_name

        return [
            {
                label: t('orderData.appraiserOffice'),
                value: valueOrDefault(serviceProvider, t('notSet')),
                greyedOut: !serviceProvider,
            },
            {
                label: t('orderData.inspectionDate'),
                value: dateOrDefault(orderOverview.inspection_date, t('notSet')),
                greyedOut: !orderOverview.inspection_date,
            },
            {
                label: t('orderData.qualityAssessmentDate'),
                value: dateOrDefault(orderOverview.quality_assessment_date, t('notSet')),
                greyedOut: !orderOverview.quality_assessment_date,
            },
            {
                label: t('orderData.reportDate'),
                value: dateOrDefault(orderOverview.report_date, t('notSet')),
                greyedOut: !orderOverview.report_date,
            },
        ]
    }

    const addContact = (contact: ContactSchema) =>
        patchOrder([
            {
                op: 'add',
                path: '/contacts/-',
                value: contact,
            },
        ])

    const updateContact = (index: number, contact: ContactSchema) =>
        patchOrder([
            {
                op: 'replace',
                path: `/contacts/${index}`,
                value: contact,
            },
        ])

    const updateAssignedCustomerUser = (assignedCustomerUserIds: number[]) => {
        patchAssignedCustomerUsers([
            {
                op: 'replace',
                path: `/assigned_customer_users`,
                value: assignedCustomerUserIds,
            },
        ])
    }

    const updateAssignedObservers = (assignedObserverIds: UUID[]) => {
        const currentObserverIds = orderOverview?.observers ?? []

        const addOperations = assignedObserverIds
            .filter((observerId) => !currentObserverIds.includes(observerId))
            .map((observerId) => ({
                op: 'add' as const,
                path: `/observers/-`,
                value: observerId,
            }))

        const removeOperations = currentObserverIds
            .map((observerId, index) => ({ observerId, index }))
            .filter(({ observerId }) => !assignedObserverIds.includes(observerId))
            .sort((a, b) => b.index - a.index)
            .map(({ index }) => ({
                op: 'remove' as const,
                path: `/observers/${index}`,
            }))

        const operations = [...removeOperations, ...addOperations]

        if (operations.length !== 0) {
            patchAssignedObservers(operations)
        }
    }

    const addAssignedObserver = (toAddObserverId: UUID) => {
        patchAssignedObservers([
            {
                op: 'add' as const,
                path: `/observers/-`,
                value: toAddObserverId,
            },
        ])
    }

    const removeAssignedObserver = (index: number) => {
        patchAssignedObservers([
            {
                op: 'remove' as const,
                path: `/observers/${index}`,
            },
        ])
    }

    const deleteContact = (index: number) =>
        patchOrder([
            {
                op: 'remove',
                path: `/contacts/${index}`,
            },
        ])

    const addMandator = (mandator: Mandator) =>
        patchOrder([
            {
                op: 'add',
                path: '/mandators/-',
                value: mandator,
            },
        ])

    const updateMandator = (index: number, mandator: Mandator) =>
        patchOrder([
            {
                op: 'replace',
                path: `/mandators/${index}`,
                value: mandator,
            },
        ])

    const deleteMandator = (index: number) =>
        patchOrder([
            {
                op: 'remove',
                path: `/mandators/${index}`,
            },
        ])

    const { filteredAndSortedThreads, isThreadsPending, isTasksPending, tasksInDone, tasksInOpen } =
        useGetSortedAndFilteredThreadsWithMetadata({ orderId })

    const downloadFileFromLink = (url: string) => {
        window.open(url, '_self')
    }

    const { generateImagesZipUrl, isGeneratingImagesZipUrl } = useGetAllOrderImagesZipUrl({ onSuccess: downloadFileFromLink })
    const { generateDocumentsZipUrl, isGeneratingDocumentsZipUrl } = useGetAllOrderDocumentsZipUrl({
        onSuccess: downloadFileFromLink,
    })

    const externalParticipantMailbox = orderOverview?.external_participant_mailbox?.proxy_email

    return {
        orderOverview,
        isOrderOverviewLoading: isOrderOverviewPending,
        getOrderKeyFigures,
        getValuationKeyFigures,
        getImages,
        uploadOrderImages,
        isUploadingOrderImages,
        deleteOrderImage,
        isPatchingOrder,
        failedImageUploads,
        numImagesToUpload,
        isUploadingOrderDocuments,
        uploadOrderDocuments,
        failedDocumentUploads,
        numDocumentsToUpload,
        updateAssignedCustomerUser,
        deleteOrderDocument,
        downloadDocument,
        addContact,
        deleteContact,
        updateContact,
        addMandator,
        deleteMandator,
        updateMandator,
        threads: filteredAndSortedThreads,
        isThreadsPending,
        isTasksPending,
        tasksInDone,
        tasksInOpen,
        customerUsers,
        isCustomerUsersPending,
        appraiserUsers,
        isAppraiserUsersPending,
        externalParticipants: externalParticipantsView?.participants ?? [],
        isExternalParticipantsPending: isExternalParticipantsInitialLoading,
        updateDocuments,
        documents,
        isDownloadingDocument: isFetchingNewOrderDocuments,
        generateDocumentsZipUrl,
        isGeneratingDocumentsZipUrl,
        generateImagesZipUrl,
        isGeneratingImagesZipUrl,
        isPatchingAssignedCustomerUsers,
        currentUser,
        updateAssignedObservers,
        isAssignedObserversPending,
        addAssignedObserver,
        removeAssignedObserver,
        latestDraftPlausibility,
        showPlausibility,
        isLatestDraftPlausibilityPending,
        valueCheckForOrder,
        externalParticipantMailbox,
    }
}
